import { Tool } from './base'
import {
  makeRenderer,
  InlineGroups,
  RenderContext,
  normalizeList,
  scalarToString,
} from './render/render-kit'
import { DatasourceMap } from './render/rtd/datasource.types'
import { AstPredicate, ValueOrList } from './render/types'

// CB Cloud Investigate supported hunt categories. Same scope as CB Response —
// bare body render with no datasource prelude or DataDisplay. Entries exist
// to expose these categories in renderSingleSourceGrouped and to prune CCM
// predicates the tool cannot search.
// network_connection: IP-only (no domain / url / dns_query).
//
// unsupportedFields on non-core categories blacklist generic-leak fields
// (process_*/target_path/hashes/registry_*) that would otherwise OR into the
// query and duplicate process_creation/file_event output (no datasource
// prelude scopes the event type).
const NON_CORE_BLACKLIST = {
  registry_event: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
  ],
  image_load: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
  dns: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
  network_connection: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
}

const carbonBlackCloudInvestigateMap: DatasourceMap = {
  'CarbonBlack Cloud - Investigate': {
    any: {
      process_creation: {},
      file_event: {},
      registry_event: { unsupportedFields: NON_CORE_BLACKLIST.registry_event },
      image_load: { unsupportedFields: NON_CORE_BLACKLIST.image_load },
      dns: {
        unsupportedFields: [...NON_CORE_BLACKLIST.dns, 'dns_answer'],
      },
      network_connection: {
        unsupportedFields: [...NON_CORE_BLACKLIST.network_connection, 'url'],
      },
    },
  },
}

export class CarbonBlackCloudInvestigateTool extends Tool {
  public readonly toolName = 'CarbonBlack Cloud - Investigate'
  public readonly toolKind = 'edr' as const
  public override readonly datasourceMap = carbonBlackCloudInvestigateMap

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const carbonBlackCloudInvestigateSearchURI =
      'cb/investigate/events/events?query='
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}${carbonBlackCloudInvestigateSearchURI}${encodedRenderedContent}`
  }

  public pivotEscape(key: string, value: string): string {
    return value
      .replace(/[-\]+&|:(){}[\]^"~*?!\\+]/g, '\\$&')
      .replace(/ /g, '\\ ')
  }

  /**
   * Carbon Black Cloud (Investigate) renderer (Lucene-like)
   *
   * Grouping
   * - InlineGroups: no root parens; nested parens as needed.
   *
   * Field mapping
   * - toolTuningFields["carbonblack_cloud_-_investigate"]; no Eta wrapping.
   *
   * Negation
   * - Uses leading minus: `-field:value`.
   * - For multi-clause negation, wrap the whole clause: `-(field:a OR field:b)`.
   *
   * Quoting & escaping
   * - Equality is quoted: `field:"value"`.
   * - Wildcard family (contains/starts/doesnotcontain) is UNQUOTED: CB Cloud
   *   treats `field:"value*"` as a literal string, so wildcard patterns must
   *   be bare. Spaces escape as `\ `; backslashes double-escape (`\\`).
   * - endswith is rewritten to eq (see tokenization note below) — it never
   *   reaches the wildcard branch.
   *
   * Operators
   * - eq:              field:"v"            | multi: (field:"v1" OR field:"v2")
   * - neq:             -field:"v"           | multi: -(field:"v1" OR field:"v2")
   * - contains:        field:v*             | multi: (field:v1* OR field:v2*)
   * - doesnotcontain:  -field:v*            | multi: -(field:v1* OR field:v2*)
   * - startswith:      field:v*             | multi: (field:v1* OR field:v2*)
   * - endswith:        rewritten to eq (token equality == basename suffix match)
   * - lt/lte/gt/gte:   range (bounds UNQUOTED; spaces would require bare escaping if ever present):
   *      lt  -> field:{* TO v}
   *      lte -> field:[* TO v]
   *      gt  -> field:{v TO *}
   *      gte -> field:[v TO *]
   *
   * Null/Empty
   * - isnull     → -field:*
   * - isnotnull  → field:*
   * - isempty    → field:""
   * - isnotempty → -field:""
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f,
      andGlue: ' AND ',
      orGlue: ' OR ',
      // renderContext.quote: default (double-quoted with escaping) is used for equality & wildcard family.
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      // Standardized list handling
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Bare term escaping
      const escapeBare = (s: string) =>
        String(s).replace(/\\/g, '\\\\').replace(/ /g, '\\ ')

      // Wildcard shaping per op
      const shape = (op: AstPredicate['op'], s: string) => {
        switch (op) {
          case 'contains':
            return `${s}*`
          case 'doesnotcontain':
            return `${s}*`
          case 'startswith':
            return `${s}*`
          case 'endswith':
            return `*${s}`
          default:
            return s
        }
      }

      // Clause builders
      const eqClause = (s: string) => `${F}:${renderContext.quote(s)}` // equals (quoted)
      const bareClause = (s: string) => `${F}:${escapeBare(s)}` // wildcard family (unquoted + escape spaces/backslashes)

      // Bool helpers
      const OR = (parts: string[]) => `(${parts.join(renderContext.orGlue)})`
      const AND = (parts: string[]) => `(${parts.join(renderContext.andGlue)})` // defined for consistency, but not utilized
      // Negation: single → `-field:term`; multi/grouped → `-( ... )`
      const NEG = (clause: string, multi: boolean) =>
        multi ? `-${clause}` : `-${clause}`

      /*  Null / Empty  */
      switch (p.op) {
        case 'isnull':
          return `-${F}:*`
        case 'isnotnull':
          return `${F}:*`
        case 'isempty':
          return `${F}:${renderContext.quote('')}`
        case 'isnotempty':
          return `-${F}:${renderContext.quote('')}`
      }

      /*  Equality / Inequality  */
      // CB Cloud Investigate tokenizes path fields, so a trailing-basename
      // match (endswith `malware.exe`) is equivalent to a token-equality
      // match. Rewrite endswith → eq here to avoid a leading-wildcard search
      // (`"*malware.exe"`) which is expensive against CB's token index.
      if (p.op === 'eq' || p.op === 'neq' || p.op === 'endswith') {
        const parts = vals.map((v) => eqClause(v))
        if (!isMulti) {
          return p.op === 'neq' ? NEG(parts[0], false) : parts[0]
        }
        const joined = OR(parts)
        return p.op === 'neq' ? NEG(joined, true) : joined
      }

      /*  String wildcard family (CBC: UNQUOTED patterns with escaped spaces)  */
      // endswith is handled above (rewritten to eq); keep the other three here.
      // CB Cloud treats `field:"value*"` as a literal string, so wildcard
      // patterns must be emitted unquoted. Spaces escape as `\ ` and
      // backslashes double-escape, matching CB Response's bareClause shape.
      if (
        p.op === 'contains' ||
        p.op === 'doesnotcontain' ||
        p.op === 'startswith'
      ) {
        const parts = vals.map((v) => bareClause(shape(p.op, v)))
        if (!isMulti) {
          return p.op === 'doesnotcontain' ? NEG(parts[0], false) : parts[0]
        }
        const joined = OR(parts)
        return p.op === 'doesnotcontain' ? NEG(joined, true) : joined
      }

      /*  Comparisons via ranges */
      if (p.op === 'lt' || p.op === 'lte' || p.op === 'gt' || p.op === 'gte') {
        const v0 = escapeBare(vals[0]) // if dates/numbers, this is a no-op except for backslashes
        switch (p.op) {
          case 'lt':
            return `${F}:{* TO ${v0}}`
          case 'lte':
            return `${F}:[* TO ${v0}]`
          case 'gt':
            return `${F}:{${v0} TO *}`
          case 'gte':
            return `${F}:[${v0} TO *]`
        }
      }

      /*  Fallback (treat like equality)  */
      if (isMulti) {
        const parts = vals.map((v) => eqClause(v))
        return OR(parts)
      }
      return eqClause(vals[0])
    },
  })

  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. NOTE: Utilize equals/not equals if searching full keyword strings. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'
}
